/**
 * The handoff code in a url: how an anonymous feed follows its person from one
 * browser to another (account.ts, routes/kindling.ts).
 *
 * The save's email link is opened by Mail, in Safari or Chrome, and the ⋯ menu
 * of Facebook's browser opens the page there too. Both are a different jar from
 * the one the work was made in, so the url carries a one-time code the backend
 * minted for the anonymous uid, and the browser that opens it signs in as that
 * uid. Pure, for the test.
 */
export const HANDOFF_PARAM = 'handoff';
/**
 * Which way the code was carried (the save's email, Facebook's ⋯ menu, Chrome, a
 * copied link, or the Day 2 email the backend sends an unconfirmed address), for
 * the landing's event.
 */
export const VIA_PARAM = 'handoff_via';
export type HandoffVia = 'email' | 'menu' | 'chrome' | 'copy' | 'reminder';
const VIAS: readonly HandoffVia[] = ['email', 'menu', 'chrome', 'copy', 'reminder'];
const SHAPE = /^[A-Za-z0-9_-]{20,64}$/;

/**
 * The code, from the url itself or from inside a `continueUrl`: Firebase's email
 * action handler may hand the page its own link with ours nested in it.
 */
export function handoffFromUrl(href: string): string | null {
  let u: URL;
  try { u = new URL(href); } catch { return null; }
  const direct = u.searchParams.get(HANDOFF_PARAM);
  if (direct && SHAPE.test(direct)) return direct;
  const nested = u.searchParams.get('continueUrl');
  if (nested && nested !== href) return handoffFromUrl(nested);
  return null;
}

/** The way the code came, from the url itself or from inside a `continueUrl`, as handoffFromUrl reads it. */
export function handoffViaFromUrl(href: string): HandoffVia | null {
  let u: URL;
  try { u = new URL(href); } catch { return null; }
  if (u.searchParams.get(HANDOFF_PARAM)) {
    const via = u.searchParams.get(VIA_PARAM) as HandoffVia | null;
    return via && VIAS.includes(via) ? via : null;
  }
  const nested = u.searchParams.get('continueUrl');
  return nested && nested !== href ? handoffViaFromUrl(nested) : null;
}

export function withHandoff(href: string, code: string | null | undefined, via?: HandoffVia): string {
  if (!code) return href;
  const u = new URL(href);
  u.searchParams.set(HANDOFF_PARAM, code);
  if (via) u.searchParams.set(VIA_PARAM, via);
  return u.toString();
}

/** The same url without our parameter; everything else (an email link's oobCode) is left as it was. */
export function withoutHandoff(href: string): string {
  const u = new URL(href);
  if (!u.searchParams.has(HANDOFF_PARAM) && !u.searchParams.has(VIA_PARAM)) return href;
  u.searchParams.delete(HANDOFF_PARAM);
  u.searchParams.delete(VIA_PARAM);
  return u.toString();
}
